import { LoanDurationType, LoanProductType } from "../../hooks/api/types";
import { LoanProductConfig } from "../dpConfig/types";

type CommonLoanInputParams = {
  amount: number;
  duration: number;
  durationType: LoanDurationType;
  interestRate: number;
};

export type LoanProductInvoiceConfig = {
  discount: {
    min: number;
    max: number;
    initial: number;
    unit: string;
  };
};

export type UserLoanCalculatorConstraintsConfig = {
  units?: {
    currency?: string;
    tenure: string;
  };
  initials?: {
    amount?: number;
    tenure?: number;
  };
  intervals?: {
    amount?: number;
    tenure?: number;
  };
  buckets: Array<{
    range: {
      amount: {
        from: number;
        to: number;
      };
    };
    constraints: {
      tenure:
        | {
            from: number;
            to: number;
          } // range of tenure
        | number; // single tenure value
      discountRate?: number;
      interestRate: number;
    };
  }>;
};

type FixedTermLoanParams = CommonLoanInputParams;
export type RevenueBasedLoanParams = CommonLoanInputParams & {
  salesStatements: RevenueBasedLoanSalesStatementParams[];
};

type RevenueBasedLoanSalesStatementParams = {
  documentId: string;
};

export type InvoiceDiscountingLoanInvoiceParams = CommonLoanInputParams & {
  invoiceId: string;
  discountRate: number;
  invoiceDueDate: Date;
  invoiceIssuedDate: Date;
  invoiceNo: string;
  vendorName: string;
};

type InvoiceDiscountingLoan = {
  invoices: InvoiceDiscountingLoanInvoiceParams[];
};

export type PayableFinancingLoanInvoiceParams = CommonLoanInputParams & {
  invoiceId: string;
  discountRate: number;
  invoiceDueDate: Date;
  invoiceIssuedDate: Date;
  invoiceNo: string;
  vendorName: string;
};

type PayableFinancingLoan = {
  invoices: PayableFinancingLoanInvoiceParams[];
};

type BuyNowPayLaterLoan = CommonLoanInputParams & {
  salesInvoices: BuyNowPayLaterLoanSalesInvoiceParams[];
};

type BuyNowPayLaterLoanSalesInvoiceParams = {
  invoiceId: string;
};

export type PosRevenueBasedLoanParams = Omit<
  CommonLoanInputParams,
  "interestRate"
> & {
  posSalesStatements: PosRevenueBasedLoanSalesStatementParams[];
  factorRate: number;
};

type PosRevenueBasedLoanSalesStatementParams = {
  posStatementId: string;
};

type CreditLineParams = {
  amount: number;
};

type FixedTermLoanReducerAction = {
  type: "SET_FIXED_TERM_LOAN";
  payload: FixedTermLoanParams;
};

type InvoiceDiscountingLoanReducerAction = {
  type: "SET_INVOICE_DISCOUNTING_LOAN";
  payload: InvoiceDiscountingLoan;
};

type PayableFinancingLoanReducerAction = {
  type: "SET_PAYABLE_FINANCING_LOAN";
  payload: PayableFinancingLoan;
};

type RevenueBasedLoanReducerAction = {
  type: "SET_REVENUE_BASED_LOAN";
  payload: RevenueBasedLoanParams;
};

type BuyNowPayLaterLoanReducerAction = {
  type: "SET_BNPL_LOAN";
  payload: BuyNowPayLaterLoan;
};

type PosRevenueBasedLoanReducerAction = {
  type: "SET_POS_REVENUE_BASED_LOAN";
  payload: PosRevenueBasedLoanParams;
};

type ReceivablesCreditLineReducerAction = {
  type: "SET_RECEIVABLES_CREDIT_LINE";
  payload: CreditLineParams;
};

type PayablesCreditLineReducerAction = {
  type: "SET_PAYABLES_CREDIT_LINE";
  payload: CreditLineParams;
};

type SetConstraintsReducerAction = {
  type: "SET_CONSTRAINTS";
  payload: LoanCalculatorState["constraints"];
};

type SetRevenueBasedLoanConstraints = {
  type: "SET_REVENUE_BASED_LOAN_CONSTRAINTS";
  payload: LoanCalculatorState["constraints"]["revenueBasedLoan"];
};

type SetLoadingAction = {
  type: "SET_LOADING";
  payload: boolean;
};

type LineOfCreditLoanCalculatorFields = {
  amount: number;
};

export type LoanCalculatorState = {
  loading: boolean;
  type?: LoanProductType;
  fixedTermLoan: FixedTermLoanParams;
  invoiceDiscountingLoan: InvoiceDiscountingLoan;
  payableFinancingLoan: PayableFinancingLoan;
  revenueBasedLoan: RevenueBasedLoanParams;
  buyNowPayLaterLoan: BuyNowPayLaterLoan;
  posRevenueBasedLoan: PosRevenueBasedLoanParams;
  receivablesCreditLine: CreditLineParams;
  payablesCreditLine: CreditLineParams;
  constraints: {
    fixedTermLoan: LoanProductConfig;
    invoiceDiscountingLoan: LoanProductConfig & LoanProductInvoiceConfig;
    receivablesCreditLine: LoanProductConfig;
    payableFinancingLoan: LoanProductConfig & LoanProductInvoiceConfig;
    payablesCreditLine: LoanProductConfig;
    revenueBasedLoan: LoanProductConfig;
    buyNowPayLaterLoan: LoanProductConfig;
    posRevenueBasedLoan: Omit<
      LoanProductConfig,
      "duration" | "interestRate"
    > & {
      duration: {
        unit: string;
      };
      factorRates: Record<number, number>;
    };
    userConstraintsConfig?: UserLoanCalculatorConstraintsConfig | null;
  };
};

export type LoanCalculatorActions = {
  setFixedTermLoan: (loan: FixedTermLoanParams) => void;
  setInvoiceDiscountingLoan: (loan: InvoiceDiscountingLoan) => void;
  setReceivablesCreditLine: (loan: LineOfCreditLoanCalculatorFields) => void;
  setPayableFinancingLoan: (loan: PayableFinancingLoan) => void;
  setPayablesCreditLine: (loan: LineOfCreditLoanCalculatorFields) => void;
  setRevenueBasedLoan: (loan: RevenueBasedLoanParams) => void;
  setPosRevenueBasedLoan: (loan: PosRevenueBasedLoanParams) => void;
  setBuyNowPayLaterLoan: (loan: BuyNowPayLaterLoan) => void;
  setLoading: (loading: boolean) => void;
  setRevenueBasedLoanConstraints: (constraints: LoanProductConfig) => void;
};

export type LoanCalculatorContextState = {
  state: LoanCalculatorState;
  actions: LoanCalculatorActions;
};

export type LoanCalculatorReducerAction =
  | FixedTermLoanReducerAction
  | RevenueBasedLoanReducerAction
  | InvoiceDiscountingLoanReducerAction
  | ReceivablesCreditLineReducerAction
  | PayableFinancingLoanReducerAction
  | PayablesCreditLineReducerAction
  | SetConstraintsReducerAction
  | SetLoadingAction
  | BuyNowPayLaterLoanReducerAction
  | PosRevenueBasedLoanReducerAction
  | SetRevenueBasedLoanConstraints;

export const LOAN_CALCULATOR_INITIAL_VALUES: LoanCalculatorContextState = {
  state: {
    loading: true,
    type: undefined,
    fixedTermLoan: {
      amount: 100_000,
      duration: 12,
      durationType: LoanDurationType.MONTHS,
      interestRate: 0.18,
    },
    revenueBasedLoan: {
      amount: 100_000,
      duration: 12,
      durationType: LoanDurationType.MONTHS,
      interestRate: 0.2,
      salesStatements: [],
    },
    posRevenueBasedLoan: {
      amount: 200_000,
      duration: 99,
      durationType: LoanDurationType.MONTHS,
      factorRate: 1.1,
      posSalesStatements: [],
    },
    invoiceDiscountingLoan: {
      invoices: [],
    },
    receivablesCreditLine: {
      amount: 100_000,
    },
    payableFinancingLoan: {
      invoices: [],
    },
    payablesCreditLine: {
      amount: 100_000,
    },
    buyNowPayLaterLoan: {
      amount: 100_000,
      duration: 3,
      durationType: LoanDurationType.MONTHS,
      interestRate: 0.2,
      salesInvoices: [],
    },
    constraints: {
      fixedTermLoan: {
        amount: {
          min: 20_000,
          max: 3_000_000,
          initial: 100_000,
          unit: "AED",
        },
        duration: {
          min: 3,
          max: 12,
          initial: 6,
          unit: "months",
        },
        interestRate: 0.18,
      },
      revenueBasedLoan: {
        amount: {
          min: 20_000,
          max: 1_000_000,
          initial: 100_000,
          unit: "AED",
        },
        duration: {
          min: 3,
          max: 12,
          initial: 6,
          unit: "months",
          isRange: true, // if false then min and max set to same value (the single duration)
        },
        interestRate: 0.18,
      },
      posRevenueBasedLoan: {
        amount: {
          min: 20_000,
          max: 1_000_000,
          initial: 100_000,
          unit: "AED",
        },
        duration: {
          unit: "months",
        },
        factorRates: {
          4: 1.1,
          6: 1.2,
          8: 1.3,
          10: 1.4,
        },
      },
      buyNowPayLaterLoan: {
        amount: {
          min: 10_000,
          max: 500_000,
          initial: 10_000,
          unit: "AED",
        },
        duration: {
          min: 3,
          max: 12,
          initial: 6,
          unit: "months",
        },
        interestRate: 0.2,
      },
      invoiceDiscountingLoan: {
        amount: {
          min: 20_000,
          max: 3_000_000,
          initial: 100_000,
          unit: "AED",
        },
        duration: {
          min: 15,
          max: 120,
          initial: 30,
          unit: "days",
        },
        interestRate: 0.18,
        discount: {
          min: 20,
          max: 90,
          initial: 90,
          unit: "%",
        },
      },
      receivablesCreditLine: {
        amount: {
          min: 20_000,
          max: 3_000_000,
          initial: 100_000,
          unit: "AED",
        },
        duration: {
          min: 15,
          max: 120,
          initial: 30,
          unit: "days",
        },
        interestRate: 0.18,
      },
      payableFinancingLoan: {
        amount: {
          min: 20_000,
          max: 3_000_000,
          initial: 100_000,
          unit: "AED",
        },
        duration: {
          min: 30,
          max: 120,
          initial: 30,
          unit: "days",
        },
        discount: {
          min: 100,
          max: 100,
          initial: 100,
          unit: "%",
        },
        interestRate: 0.18,
      },
      payablesCreditLine: {
        amount: {
          min: 20_000,
          max: 3_000_000,
          initial: 100_000,
          unit: "AED",
        },
        duration: {
          min: 30,
          max: 120,
          initial: 30,
          unit: "days",
        },
        interestRate: 0.18,
      },
      userConstraintsConfig: null,
    },
  },
  actions: {
    setFixedTermLoan: () => {},
    setInvoiceDiscountingLoan: () => {},
    setReceivablesCreditLine: () => {},
    setPayableFinancingLoan: () => {},
    setPayablesCreditLine: () => {},
    setRevenueBasedLoan: () => {},
    setPosRevenueBasedLoan: () => {},
    setBuyNowPayLaterLoan: () => {},
    setLoading: () => {},
    setRevenueBasedLoanConstraints: () => {},
  },
};
